import type { HttpPort, IRecordService, Json, ListQuery, ListResult, RecordData } from "../contracts";
import { listQueryToBody, modelKeyToPath } from "./query";

// The generic CRUD backend (`/api/records/*` → `ede.*` commands) speaks raw
// repo rows: identity lives in `record_uuid`, and the mutating endpoints wrap
// the row in a `{success, record}` envelope. The `RecordData` contract this
// service implements promises `id` on every row — normalise here, once, so
// no consumer ever reaches for `record_uuid` or unwraps envelopes itself.

function normalizeRow(raw: unknown): RecordData {
    const row = (raw && typeof raw === "object" ? raw : {}) as Record<string, Json>;
    const id = row.id ?? row.record_uuid;
    return (id == null ? row : { ...row, id: String(id) }) as RecordData;
}

function unwrap(res: unknown): RecordData {
    const envelope = (res && typeof res === "object" ? res : {}) as { record?: unknown };
    return normalizeRow(envelope.record ?? res);
}

export class RecordService implements IRecordService {
    constructor(private readonly http: HttpPort) {}

    async create(modelKey: string, values: Json): Promise<RecordData> {
        const res = await this.http.post<unknown>(`/api/records/${modelKeyToPath(modelKey)}`, values);
        return unwrap(res);
    }

    async read(modelKey: string, recordUuid: string): Promise<RecordData> {
        const res = await this.http.get<unknown>(`/api/records/${modelKeyToPath(modelKey)}/${encodeURIComponent(recordUuid)}`);
        return unwrap(res);
    }

    async update(modelKey: string, recordUuid: string, values: Json): Promise<RecordData> {
        const res = await this.http.put<unknown>(`/api/records/${modelKeyToPath(modelKey)}/${encodeURIComponent(recordUuid)}`, values);
        return unwrap(res);
    }

    async delete(modelKey: string, recordUuid: string): Promise<void> {
        await this.http.delete(`/api/records/${modelKeyToPath(modelKey)}/${encodeURIComponent(recordUuid)}`);
    }

    async search(modelKey: string, query: ListQuery): Promise<ListResult> {
        const res = await this.http.post<{ records?: unknown[]; total?: number }>(
            `/api/records/${modelKeyToPath(modelKey)}/search`,
            listQueryToBody(query),
        );
        // Generic CRUD search has no grouped shape — that's the
        // presentation action_records path (ActionService.listRecords).
        return { records: (res.records ?? []).map(normalizeRow), total: res.total };
    }
}
